/**
 * Audioguías · production catalog.
 *
 * Lists EVERY tour of the unified catalog (custom + Bokun) with the state of
 * its audio guide, so a new tour can be turned into a guide in one click.
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Headphones, Loader2, Plus, Search, Images, BarChart3 } from "lucide-react";

import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  listAudioGuideCatalog,
  createAudioGuide,
  getAudioGuideStats,
  type CatalogGuideRow,
} from "@/lib/audio-guides-admin.functions";
import {
  EXPERIENCE_TYPE_LABELS,
  GUIDE_STATUS_LABELS,
  LANG_LABELS,
  formatDuration,
  type ExperienceType,
} from "@/lib/audio-guide-templates";

export const Route = createFileRoute("/_authenticated/admin/audioguias/")({
  head: () => ({
    meta: [
      { title: "Audioguías · Producción" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

function guessType(title: string, destination: string | null): ExperienceType {
  const t = `${title} ${destination ?? ""}`.toLowerCase();
  if (/(chich|uxmal|tulum|coba|cobá|palenque|teotihuac|ruina|zona arqueo|piramide|pirámide)/.test(t))
    return "archaeological";
  if (/(cenote|laguna|bacalar|snorkel|buceo|isla|playa|catamar|arrecife)/.test(t)) return "water";
  if (/(reserva|selva|manglar|sian|celest|flamenc|biosfera|naturaleza|cascada)/.test(t))
    return "nature";
  if (/(gastro|comida|cocina|mercado|tequila|mezcal|degust)/.test(t)) return "food";
  if (/(city tour|centro histórico|ciudad|barrio|museo|pueblo mágico)/.test(t)) return "urban";
  return "generic";
}

function Page() {
  const qc = useQueryClient();
  const listFn = useServerFn(listAudioGuideCatalog);
  const createFn = useServerFn(createAudioGuide);
  const statsFn = useServerFn(getAudioGuideStats);

  const [search, setSearch] = useState("");
  const [onlyWithGuide, setOnlyWithGuide] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const catalog = useQuery({
    queryKey: ["admin", "audioguides", "catalog"],
    queryFn: () => listFn({ data: {} }),
    staleTime: 60_000,
  });

  const stats = useQuery({
    queryKey: ["admin", "audioguides", "stats"],
    queryFn: () => statsFn({ data: { days: 90 } }),
    staleTime: 120_000,
  });

  const create = useMutation({
    mutationFn: (row: CatalogGuideRow) =>
      createFn({
        data: {
          tourSlug: row.tourSlug,
          tourSource: row.tourSource === "bokun" ? "bokun" : "custom",
          tourTitle: row.tourTitle,
          destination: row.destination,
          experienceType: guessType(row.tourTitle, row.destination),
        },
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["admin", "audioguides", "catalog"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo crear la audioguía"),
  });

  const rows = useMemo(() => {
    const all = catalog.data?.rows ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (onlyWithGuide && !r.guide) return false;
      if (!q) return true;
      return (
        r.tourTitle.toLowerCase().includes(q) ||
        r.tourSlug.toLowerCase().includes(q) ||
        (r.destination ?? "").toLowerCase().includes(q)
      );
    });
  }, [catalog.data, search, onlyWithGuide]);

  const withGuide = (catalog.data?.rows ?? []).filter((r) => r.guide).length;
  const published = (catalog.data?.rows ?? []).filter((r) =>
    r.guide?.languages.some((l) => l.status === "published"),
  ).length;

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8">
      <AdminNav />

      <header className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-black text-foreground">
            <Headphones className="h-6 w-6 text-primary" />
            Audioguías · Producción
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Todo el catálogo, cualquier tour. La IA solo se usa aquí para redactar borradores y
            generar el MP3 una vez; los turistas escuchan archivos ya guardados.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/fotos-viajeros">
              <Images className="mr-1.5 h-4 w-4" />
              Fotos de viajeros
              {stats.data?.photos.pending ? (
                <Badge className="ml-2">{stats.data.photos.pending}</Badge>
              ) : null}
            </Link>
          </Button>
        </div>
      </header>

      <section className="mt-6 grid gap-3 sm:grid-cols-4">
        <Stat label="Tours en catálogo" value={(catalog.data?.rows ?? []).length} />
        <Stat label="Con audioguía" value={withGuide} />
        <Stat label="Publicadas" value={published} />
        <Stat
          label="Aperturas (90 d)"
          value={stats.data?.byEvent.find((e) => e.eventType === "guide_opened")?.count ?? 0}
          icon={<BarChart3 className="h-4 w-4 text-primary" />}
        />
      </section>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar tour, destino o slug…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <Button
          variant={onlyWithGuide ? "default" : "outline"}
          size="sm"
          onClick={() => setOnlyWithGuide((v) => !v)}
        >
          Solo con audioguía
        </Button>
      </div>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

      {catalog.isLoading ? (
        <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando catálogo…
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {rows.map((row) => (
            <li
              key={row.tourSlug}
              className="rounded-2xl border border-border bg-card p-4 sm:flex sm:items-center sm:gap-4"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-foreground">{row.tourTitle}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {row.destination ? `${row.destination} · ` : ""}
                  {row.tourSlug} · {row.tourSource === "bokun" ? "Bokun" : "Propio"}
                </p>
                {row.guide && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline">
                      {EXPERIENCE_TYPE_LABELS[row.guide.experienceType]}
                    </Badge>
                    <Badge variant="secondary">
                      {GUIDE_STATUS_LABELS[
                        row.guide.status as keyof typeof GUIDE_STATUS_LABELS
                      ] ?? row.guide.status}
                    </Badge>
                    {row.guide.languages.map((l) => (
                      <Badge
                        key={l.id}
                        variant={l.status === "published" ? "default" : "outline"}
                        title={`${l.chaptersWithAudio}/${l.chapterCount} capítulos con audio`}
                      >
                        {LANG_LABELS[l.lang]} · {l.chaptersWithAudio}/{l.chapterCount} ·{" "}
                        {formatDuration(l.totalDurationSeconds)}
                      </Badge>
                    ))}
                    {row.guide.languages.length === 0 && (
                      <Badge variant="outline">Sin idiomas</Badge>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-3 flex shrink-0 gap-2 sm:mt-0">
                {row.guide ? (
                  <Button size="sm" asChild>
                    <Link
                      to="/admin/audioguias/$id"
                      params={{ id: row.guide.id }}
                    >
                      Producir
                    </Link>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={create.isPending}
                    onClick={() => create.mutate(row)}
                  >
                    {create.isPending ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-1.5 h-4 w-4" />
                    )}
                    Crear audioguía
                  </Button>
                )}
              </div>
            </li>
          ))}
          {rows.length === 0 && (
            <li className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Sin resultados.
            </li>
          )}
        </ul>
      )}
    </main>
  );
}

function Stat({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        {label}
      </p>
      <p className="mt-1 text-2xl font-black tabular-nums text-foreground">{value}</p>
    </div>
  );
}
